<template>
  <span v-if="phase !== 'hidden'" class="countdown">
    <template v-if="phase === 'ended'">
      <span class="cd-label">活動結束</span>
    </template>
    <template v-else>
      <span class="cd-label">{{ label }}</span>
      <span class="cd-value">{{ display }}</span>
      <span v-if="showDayUnit" class="cd-unit">天</span>
    </template>
  </span>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import api from "@/api/client";

/**
 * Three-phase event countdown. Reads `event_start_at` / `event_end_at`
 * from `/api/event-config` and:
 *
 *   · before start, ≥ 1 day to go   → 「開始前 N 天」
 *   · before start, < 1 day to go   → 「開始前 hh:mm:ss」
 *   · within event, ≥ 1 day to go   → 「倒數 N 天」
 *   · within event, < 1 day to go   → 「倒數 hh:mm:ss」
 *   · after end                      → 「活動結束」(plain text, no number)
 *   · either NULL                    → hidden (organiser hasn't set yet)
 *
 * Granularity switches at the 24-hour boundary — second-level precision
 * only matters once the user is in the same day as the target. Above
 * that, the day count alone is what the marketing copy cares about.
 */
type Phase = "pre" | "during" | "ended" | "hidden";

const startAt = ref<Date | null>(null);
const endAt   = ref<Date | null>(null);
const now     = ref(new Date());

let tickTimer: ReturnType<typeof setInterval> | null = null;

onMounted(async () => {
  try {
    const r = await api.get("/event-config");
    if (r.data?.event_start_at) startAt.value = new Date(r.data.event_start_at);
    if (r.data?.event_end_at)   endAt.value   = new Date(r.data.event_end_at);
  } catch {
    /* leave nulls; phase falls back to hidden */
  }
  tickTimer = setInterval(() => { now.value = new Date(); }, 1000);
});

onUnmounted(() => {
  if (tickTimer) clearInterval(tickTimer);
});

const phase = computed<Phase>(() => {
  const s = startAt.value, e = endAt.value;
  if (!s || !e) return "hidden";
  const t = now.value.getTime();
  if (t < s.getTime()) return "pre";
  if (t <= e.getTime()) return "during";
  return "ended";
});

const label = computed(() =>
  phase.value === "pre" ? "開始前" : "倒數"
);

interface Parts { days: number; hours: number; minutes: number; seconds: number; }

const parts = computed<Parts | null>(() => {
  const target =
    phase.value === "pre" ? startAt.value :
    phase.value === "during" ? endAt.value :
    null;
  if (!target) return null;
  const diff = Math.max(0, target.getTime() - now.value.getTime());
  return {
    days:    Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours:   Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
});

const showDayUnit = computed(() => (parts.value?.days ?? 0) >= 1);

const display = computed(() => {
  const p = parts.value;
  if (!p) return "";
  if (p.days >= 1) return String(p.days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(p.hours)}:${pad(p.minutes)}:${pad(p.seconds)}`;
});
</script>

<style scoped>
.countdown {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  font-size: 12px;
  color: var(--text-secondary);
  flex-shrink: 0;
  white-space: nowrap;
}

.cd-label,
.cd-unit {
  white-space: nowrap;
}

.cd-value {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.5px;
  color: var(--text-primary);
}

.cd-unit { color: var(--text-secondary); }

@media (max-width: 360px) {
  .cd-label { display: none; }
  .countdown { font-size: 11px; }
}
</style>
